import * as Yup from 'yup';
import {
  INTERACTIVE_HEADER_TYPES,
  INTERACTIVE_LIMITS,
  INTERACTIVE_TYPES,
  tInteractiveOutbound,
} from '../../../../Chat.types';
import { checkCtaUrlSafety, totalListRowCount, utf16Count } from './InteractiveAction.functions';

// Pattern: Yup.lazy() inspects the runtime value and returns the correct
// schema per discriminated-union variant. Flat `.when()` chains get tangled
// when more than one field depends on the discriminator — lazy() keeps each
// variant schema readable on its own and avoids the cross-branch drift that
// Yup's `.when` produces on nested unions. See
// [class-validator#2409](https://github.com/typestack/class-validator/issues/2409)
// for the analogous backend pattern.

const mediaRefSchema = Yup.object({
  id: Yup.string().optional(),
  link: Yup.string().optional(),
})
  .noUnknown()
  .default(undefined);

const mediaHeaderSchema = Yup.object({
  type: Yup.string().oneOf([...INTERACTIVE_HEADER_TYPES]).required(),
  text: Yup.string()
    .optional()
    .test(
      'headerTextLen',
      `Max ${INTERACTIVE_LIMITS.HEADER_TEXT} code units`,
      (v) => utf16Count(v) <= INTERACTIVE_LIMITS.HEADER_TEXT,
    ),
  image: mediaRefSchema.optional(),
  video: mediaRefSchema.optional(),
  document: mediaRefSchema.optional(),
})
  .noUnknown()
  .default(undefined);

const textHeaderSchema = Yup.object({
  type: Yup.string().oneOf(['text']).required(),
  text: Yup.string()
    .required()
    .test(
      'headerTextLen',
      `Max ${INTERACTIVE_LIMITS.HEADER_TEXT} code units`,
      (v) => utf16Count(v) <= INTERACTIVE_LIMITS.HEADER_TEXT,
    ),
})
  .noUnknown()
  .default(undefined);

const buttonSchema = Yup.object({
  id: Yup.string()
    .required()
    .test(
      'buttonIdLen',
      `Max ${INTERACTIVE_LIMITS.BUTTON_ID} code units`,
      (v) => utf16Count(v) <= INTERACTIVE_LIMITS.BUTTON_ID,
    ),
  title: Yup.string()
    .required()
    .test(
      'buttonTitleLen',
      `Max ${INTERACTIVE_LIMITS.BUTTON_TITLE} code units`,
      (v) => utf16Count(v) <= INTERACTIVE_LIMITS.BUTTON_TITLE,
    ),
}).noUnknown();

const buttonActionSchema = Yup.object({
  buttons: Yup.array()
    .of(buttonSchema)
    .min(INTERACTIVE_LIMITS.BUTTON_COUNT_MIN)
    .max(INTERACTIVE_LIMITS.BUTTON_COUNT_MAX)
    .required(),
}).noUnknown();

const ctaUrlActionSchema = Yup.object({
  display_text: Yup.string()
    .required()
    .test(
      'ctaDisplayLen',
      `Max ${INTERACTIVE_LIMITS.CTA_DISPLAY_TEXT} code units`,
      (v) => utf16Count(v) <= INTERACTIVE_LIMITS.CTA_DISPLAY_TEXT,
    ),
  url: Yup.string()
    .required()
    .max(INTERACTIVE_LIMITS.CTA_URL)
    .test(
      'ctaUrlSafe',
      'URL must be https with a valid public hostname (e.g. https://leadsales.io)',
      (v) => {
        if (!v) return false;
        return checkCtaUrlSafety(v).ok;
      },
    ),
}).noUnknown();

const listRowSchema = Yup.object({
  id: Yup.string()
    .required()
    .test(
      'rowIdLen',
      `Max ${INTERACTIVE_LIMITS.LIST_ROW_ID} code units`,
      (v) => utf16Count(v) <= INTERACTIVE_LIMITS.LIST_ROW_ID,
    ),
  title: Yup.string()
    .required()
    .test(
      'rowTitleLen',
      `Max ${INTERACTIVE_LIMITS.LIST_ROW_TITLE} code units`,
      (v) => utf16Count(v) <= INTERACTIVE_LIMITS.LIST_ROW_TITLE,
    ),
  description: Yup.string()
    .optional()
    .test(
      'rowDescLen',
      `Max ${INTERACTIVE_LIMITS.LIST_ROW_DESCRIPTION} code units`,
      (v) => utf16Count(v) <= INTERACTIVE_LIMITS.LIST_ROW_DESCRIPTION,
    ),
}).noUnknown();

const listSectionSchema = Yup.object({
  title: Yup.string()
    .optional()
    .test(
      'sectionTitleLen',
      `Max ${INTERACTIVE_LIMITS.LIST_SECTION_TITLE} code units`,
      (v) => utf16Count(v) <= INTERACTIVE_LIMITS.LIST_SECTION_TITLE,
    ),
  rows: Yup.array().of(listRowSchema).min(1).required(),
}).noUnknown();

const listActionSchema = Yup.object({
  button_text: Yup.string()
    .required()
    .test(
      'listButtonLen',
      `Max ${INTERACTIVE_LIMITS.LIST_BUTTON_TEXT} code units`,
      (v) => utf16Count(v) <= INTERACTIVE_LIMITS.LIST_BUTTON_TEXT,
    ),
  sections: Yup.array()
    .of(listSectionSchema)
    .min(INTERACTIVE_LIMITS.LIST_SECTION_COUNT_MIN)
    .max(INTERACTIVE_LIMITS.LIST_SECTION_COUNT_MAX)
    .required()
    .test(
      'listRowsTotal',
      `Total rows across all sections must be ${INTERACTIVE_LIMITS.LIST_ROWS_TOTAL_MIN}–${INTERACTIVE_LIMITS.LIST_ROWS_TOTAL_MAX}`,
      (sections) => {
        const total = totalListRowCount(sections as { rows: unknown[] }[] | undefined);
        return (
          total >= INTERACTIVE_LIMITS.LIST_ROWS_TOTAL_MIN &&
          total <= INTERACTIVE_LIMITS.LIST_ROWS_TOTAL_MAX
        );
      },
    ),
}).noUnknown();

const footerSchema = Yup.string()
  .optional()
  .test(
    'footerLen',
    `Max ${INTERACTIVE_LIMITS.FOOTER} code units`,
    (v) => utf16Count(v) <= INTERACTIVE_LIMITS.FOOTER,
  );

const buttonOutboundSchema = Yup.object({
  type: Yup.string().oneOf(['button']).required(),
  header: mediaHeaderSchema,
  body: Yup.string()
    .required()
    .test(
      'buttonBodyLen',
      `Max ${INTERACTIVE_LIMITS.BUTTON_BODY} code units`,
      (v) => utf16Count(v) <= INTERACTIVE_LIMITS.BUTTON_BODY,
    ),
  footer: footerSchema,
  action: buttonActionSchema.required(),
}).noUnknown();

const ctaUrlOutboundSchema = Yup.object({
  type: Yup.string().oneOf(['cta_url']).required(),
  header: mediaHeaderSchema,
  body: Yup.string()
    .required()
    .test(
      'ctaBodyLen',
      `Max ${INTERACTIVE_LIMITS.CTA_URL_BODY} code units`,
      (v) => utf16Count(v) <= INTERACTIVE_LIMITS.CTA_URL_BODY,
    ),
  footer: footerSchema,
  action: ctaUrlActionSchema.required(),
}).noUnknown();

const listOutboundSchema = Yup.object({
  type: Yup.string().oneOf(['list']).required(),
  header: textHeaderSchema,
  body: Yup.string()
    .required()
    .test(
      'listBodyLen',
      `Max ${INTERACTIVE_LIMITS.LIST_BODY} code units`,
      (v) => utf16Count(v) <= INTERACTIVE_LIMITS.LIST_BODY,
    ),
  footer: footerSchema,
  action: listActionSchema.required(),
}).noUnknown();

export const interactiveOutboundSchema = Yup.lazy((value: unknown) => {
  const record = value as { type?: string } | undefined;
  switch (record?.type) {
    case 'button':
      return buttonOutboundSchema;
    case 'cta_url':
      return ctaUrlOutboundSchema;
    case 'list':
      return listOutboundSchema;
    default:
      // Let the catch-all surface a clear error pointing at `type`.
      return Yup.object({
        type: Yup.string().oneOf([...INTERACTIVE_TYPES]).required(),
      });
  }
});

// Type witness — the schema above should validate the same shape as
// tInteractiveOutbound. If you change one, change the other and watch the
// drift canary test fail loudly.
export type tInteractiveOutboundSchemaShape = tInteractiveOutbound;
